import { Injectable } from '@angular/core';
import { makeDropdownOption } from '@app/functions/make-dropdown-option';
import { GlobalSearchStatusEnum } from '@app/models/enums/global-search-status.enum';
import { ProductSubtypeEnum } from '@app/models/enums/product-subtype.enum';
import { ProductTypeEnum } from '@app/models/enums/product-type.enum';
import { SearchStatusEnum } from '@app/models/enums/search-status.enum';
import { AssayCatalog } from '@app/models/interfaces/assay-catalog.interface';
import { Dropdown } from '@app/models/interfaces/Dropdown';
import { ChangeFilterParams, IModalFilters, INavigator, ISearchFilters, IStoreService, ITempFilters } from '@app/models/interfaces/ISearchFilters';
import { ResultCountInterface } from '@app/models/interfaces/result-count.interface';
import { SearchCountsInterface } from '@app/models/interfaces/search-counts.interface';
import { SearchParamsInterface } from '@app/models/interfaces/search-params.interface';
import { SearchResultsInterface } from '@app/models/interfaces/search-results.interface';
import { TaqmanProductInterface } from '@app/models/interfaces/taqman-product.interface';
import { FilterSearchRequestInterface } from '@app/models/requests/filter-search-request.interface';
import { TaqmanProductCountRequestInterface, TaqmanSearchRequestInterface } from '@app/models/requests/taqman-search-request.interface';
import { TaqmanGlobalSearchResponseInterface } from '@app/models/responses/taqman-search-response.interface';
import { SelectItem } from '@app/modules/tf-filters';
import { TfNotificationsService } from '@app/modules/tf-notifications';
import { GXProductInterface } from '@app/products/ge/arrays/models/interfaces/gx-product.interface';
import { GEProductInterface } from '@app/products/ge/assays/models/interfaces/ge-product.interface';
import { MicrornaArrayProductInterface } from '@app/products/microrna/microrna-array/models/interfaces/microrna-array-product.interface';
import { SNPProductInterface } from '@app/products/snp/models/interfaces/snp-product.interface';
import { TfApiService } from '@app/services/api.service';
import { SearchService } from '@app/services/search.service';
import { BehaviorSubject, Observable } from 'rxjs';

const initialSearchParams: SearchParamsInterface = {
    filters: '',
    keyword: '',
    productType: null,
    productSubtype: null,
    sortOrder: '',
    sortBy: '',
    limit: '25',
    offset: '0',
}

const initialFilters: ITempFilters = {
    filters: [],
    filterState: [],
    selectedOptions: [],
    geneFilterKeyword: [],
    overlayVisible: [],
    isFilterPillOpened: false
}

const modalFilters: IModalFilters = {
    filters: [],
    totalResultsCount: 0
}

const initialProductCountParams: TaqmanProductCountRequestInterface = {
    keyword: '*',
    productType: null,
    productSubType: null,
    species: ''
}

const initialSearchResultCounts: SearchCountsInterface = {
    assayCount: 0,
    arrayCount: 0
}

const searchKeywordRegex = /^[0-9a-zA-Z\-_.,:;()]+$/;

@Injectable({
    providedIn: 'root'
})
export class StoreService {

    private _tempFilters$ = new BehaviorSubject<ITempFilters>(initialFilters);
    private _modalFilters$ = new BehaviorSubject<IModalFilters>(modalFilters);

    tempFilters$: Observable<ITempFilters> = this._tempFilters$;
    modalFilters$: Observable<IModalFilters> = this._modalFilters$;

    searchStatus$ = new BehaviorSubject<SearchStatusEnum>(SearchStatusEnum.initial);
    searchParams$ = new BehaviorSubject<SearchParamsInterface>(initialSearchParams);
    searchResults$ = new BehaviorSubject<SearchResultsInterface>(null);

    private _searchResultCountsState$ = new BehaviorSubject<SearchCountsInterface>(initialSearchResultCounts);
    public searchResultCountsState$: Observable<SearchCountsInterface> = this._searchResultCountsState$;

    selectedResults$ = new BehaviorSubject<any>(null);
    allSelectedResults$ = new BehaviorSubject<boolean>(false);

    private _globalSearchResults$ = new BehaviorSubject<TaqmanGlobalSearchResponseInterface>(null);
    get globalSearchResults$(): Observable<TaqmanGlobalSearchResponseInterface> {
        return this._globalSearchResults$;
    }

    private _globalSearchStatus$ = new BehaviorSubject<GlobalSearchStatusEnum>(GlobalSearchStatusEnum.initial);
    get globalSearchStatus$(): Observable<GlobalSearchStatusEnum> {
        return this._globalSearchStatus$;
    }


    limitWarning = 4;
    warningShown: boolean = false;
    firstShown: boolean = false;
    firstFiltersLoaded: boolean = true;
    initialGeneFilters: any[] = [];

    constructor(
        private apiService: TfApiService,
        private tfNotificationsService: TfNotificationsService,
        private searchService: SearchService
    ) {
    }

    setSelectedFilters(nextState: SelectItem): void {
        const currentFilters = [...this.getFilters()];
        const newCurrentFilters = this._modifiedCurrentFilter(currentFilters, nextState);

        this.setLocalFilters({ filters: newCurrentFilters });
        this.changeFilterState(nextState);
    }

    _modifiedCurrentFilter(currentFilters: SelectItem[], nextState: SelectItem) {
        const mergedFilters = [...currentFilters, nextState];
        const hashMap = new Map();
        const newCurrentFilters = []

        for (const filter of mergedFilters) {
            const key = `${filter['code']}~${filter['app']}`;

            if (filter['code'] === nextState['code'] && filter['app'] === nextState['app'] && nextState['isChecked'] === false) {
                hashMap.delete(key);
                continue;
            }
            hashMap.set(key, filter);
        }

        for (const [_, value] of hashMap.entries()) {
            newCurrentFilters.push(value)
        }

        return newCurrentFilters;
    }

    changeFilterState(clickedOption: SelectItem) {
        const filterState = this.getLocalFilters('filterState');

        filterState.forEach((filterState: ISearchFilters) => {
            filterState.navigators.forEach((navigator: INavigator) => {
                if (navigator.solrFacetName === clickedOption['code'] && navigator.name === clickedOption.value.code) {
                    navigator.checked = clickedOption['isChecked'];
                }
            })
        })

        this.setLocalFilters({ filterState });
    }

    removeTempFilter(nextState: SelectItem): void {
        const oldFilters = [...this.getFilters()];
        const filteredFilters = oldFilters.filter(filter => filter['code'] && nextState['code'] && filter['app'] !== nextState['app']);

        this.setLocalFilters({ filters: filteredFilters });
        this.changeSelectedOptions(nextState);
        this.changeFilterState({ ...nextState, isChecked: false });
        this.setFilters();
    }

    changeSelectedOptions(nextState: SelectItem) {
        const selectedOptions = this.getLocalFilters('selectedOptions');

        for (let i = 0; i < selectedOptions.length; i++) {
            selectedOptions[i].forEach((selectedOption, index) => {
                if (selectedOption['code'] === nextState['code'] && selectedOption.app === nextState['app']) {
                    selectedOptions[i].splice(index, 1);
                }
            })
        }

        this.setLocalFilters({ selectedOptions: selectedOptions });
    }

    resetTempFilters(): void {
        this.setLocalFilters({ filters: [], selectedOptions: [], filterState: [] })
        this.setFilters();
    }

    setFilters(filterIndex?: number, nextState?: ChangeFilterParams): void {
        if (nextState?.clickedOption) this.setSelectedFilters(nextState.clickedOption);
        const newFilters = this._manipulateSelectedFilters();
        const strFilters = this._getFilteredString(newFilters);

        this.changeSearchParams({
            filters: strFilters,
            offset: '0',
            limit: parseInt(this.searchParams$.getValue().limit) < 25 ? '25' : this.searchParams$.getValue().limit
        });
        this.doSearch();
        this.setFilterPillState();

        if (nextState) {
            const selectedOptions = this.getLocalFilters('selectedOptions');
            nextState.selectedOptions.forEach(selectedOption => {
                selectedOption['isChecked'] = true;
            })
            selectedOptions[filterIndex] = nextState.selectedOptions;

            this.setLocalFilters({ selectedOptions: selectedOptions });
        }
    }

    setSelectedOptions(filterIndex: number, nextState: SelectItem[]) {
        if (nextState) {
            const selectedOptions = this.getLocalFilters('selectedOptions');
            selectedOptions[filterIndex] = nextState;

            this.setLocalFilters({ selectedOptions: selectedOptions });
        }
    }

    setSearchValue(searchValue: string) {
        this.searchParams$.next({ ...this.searchParams$.getValue(), filters: '', keyword: searchValue, offset: '0' });
    }

    setFilterState(filterState: Array<ISearchFilters>): void {
        this.setLocalFilters({ filterState })
    }

    setDefaultState(filterLength: number): void {
        let emptySelectedOptions = new Array(filterLength);
        emptySelectedOptions = Array.from(emptySelectedOptions, () => []);

        this.setLocalFilters({ selectedOptions: emptySelectedOptions });
    }

    changeSearchType(nextState: IStoreService): void {
        this.searchParams$.next({
            ...this.searchParams$.getValue(),
            keyword: nextState.keyword,
            filters: '',
            offset: '0',
            productType: nextState.productType,
            productSubtype: nextState.productSubtype
        })
        this.setFilterPillState();
        this.resetFilters();
    }

    changeSearchParams(searchParams: SearchParamsInterface): void {
        this.searchParams$.next({ ...this.searchParams$.getValue(), ...searchParams });
    }

    doSearchProducts(): void {

        this.searchStatus$.next(SearchStatusEnum.searching);

        this.resetSearchResults();

        const searchParams: SearchParamsInterface = this.searchParams$.getValue();
        const filterState: ISearchFilters[] = this.getLocalFilters('filterState');

        if (searchKeywordRegex.test(searchParams.keyword) == false && searchParams.keyword != '*') {
            searchParams.keyword = searchParams.keyword.replace(/[^0-9a-zA-Z\-_.,:;()\s]/g, '');
        }

        this.searchService.doSearch(searchParams, filterState).subscribe({
            next: (results) => {

                if (this.firstFiltersLoaded) {
                    this.initialGeneFilters = results.filters.filter(filter => filter.name === 'Genes')[0];
                }

                this.firstFiltersLoaded = false;

                this.updateResultCount({
                    productType: searchParams.productType,
                    productSubType: searchParams.productSubtype,
                    count: results.totalHits
                })

                this.searchResults$.next(results);

                this.setFilterState(results.filters);

                if (results.totalHits > 0) {

                    if (results.suggestedKeywords && results.suggestedKeywords.length > 0) {
                        this.searchStatus$.next(SearchStatusEnum.doneWithSuggestions)
                    } else {
                        this.searchStatus$.next(SearchStatusEnum.doneWithResults);
                        this.unselectAllProducts();
                    }

                } else if (results.totalHits === 0 && filterState && filterState.length > 0) {
                    this.searchStatus$.next(SearchStatusEnum.doneWithNoResultsFilters);
                } else {
                    this.searchStatus$.next(SearchStatusEnum.doneWithNoResults);
                }

                this.firstFiltersLoaded = false;

            },
            error: () => {
                this.searchStatus$.next(SearchStatusEnum.error);
            }
        });

    }

    doSearchProductCounts(): void {

        this._globalSearchStatus$.next(GlobalSearchStatusEnum.searching);

        const searchParams = this.searchParams$.getValue();
        const productTypeParsed = searchParams.productType && searchParams.productType.toLowerCase() in ['ge', 'gx'] ? 'ge' : null;

        if (searchKeywordRegex.test(searchParams.keyword) == false  && searchParams.keyword != '*') {
            searchParams.keyword = searchParams.keyword.replace(/[^0-9a-zA-Z\-_.,:;()\s]/g, '');
        }

        const taqmanProductCountParams: TaqmanProductCountRequestInterface = {
            keyword: searchParams.keyword,
            productType: ProductTypeEnum[productTypeParsed]
        };

        this.apiService.searchProductCounts(taqmanProductCountParams).subscribe({
                next: (response) => {
                    const newResults: TaqmanGlobalSearchResponseInterface = {
                        client: response.client,
                        countryCode: response.countryCode,
                        detectedProductSubType: response.detectedProductSubType,
                        exactMatch: response.exactMatch,
                        fastQueryTime: response.fastQueryTime,
                        geneData: response.geneData,
                        keyword: searchParams.keyword,
                        productTypeHits: response.productTypeHits,
                        productTypes: response.productTypes,
                        querySuccess: response.querySuccess,
                        taqManSearch: response.taqManSearch,
                        totalTime: response.totalTime,
                        species: response.species,
                        suggestedKeywords: response.suggestedKeywords,
                        keywordFound: response.keywordFound,
                        suggestedKeywordsArray: this.parseSuggestedKeywords(response.suggestedKeywords),
                        keywordUsed: response.keyword
                    };

                    this._globalSearchResults$.next(newResults);

                    if (response.keywordFound === true) {
                        const validatedType = searchParams.productType ? searchParams.productType : searchParams.productSearch ? searchParams.productSearch : null;
                        this.validateResults(response, validatedType);
                    } else if (response.keywordFound === false && response.suggestedKeywords && response.keywordUsed !== response.keyword && response.suggestedKeywords.length > 0) {
                        this._globalSearchStatus$.next(GlobalSearchStatusEnum.doneWithTypo);
                    } else if (response.keywordFound === false && (response.suggestedKeywords === null || response.suggestedKeywords.length === 0)) {
                        this._globalSearchStatus$.next(GlobalSearchStatusEnum.doneWithZeroResults);
                    }
                },
                error: (err) => {
                    console.log(err);
                    this.tfNotificationsService.showErrorHtml('There has been an error, please try again later.', 'Error',
                        { toastClass: 'availability-notification', timeOut: 10000, extendedTimeOut: 10000 }
                    );
                    this._globalSearchStatus$.next(GlobalSearchStatusEnum.error);
                }
            }
        );
    }

    validateResults(response: TaqmanGlobalSearchResponseInterface, productType: string): void {
        if (response.keywordFound === true && response.productTypeHits) {
            if (productType && productType.toLowerCase() !== 'apps') {
                let parsedProductType = [];
                switch (productType) {
                    case 'ge' :
                        parsedProductType = ['TAQMAN_GE'];
                        break;
                    case 'gx' :
                        parsedProductType = ['ARRAY_GX'];
                        break;
                    case 'microrna' :
                        parsedProductType = ['ADVANCED_MIRNA', 'MIRNA_CONTROL_ASSAY', 'PRI_MIRNA', 'ARRAY_MIRNA'];
                        break;
                    case 'mutation' :
                        parsedProductType = ['DPCR', 'CPCR'];
                        break;
                    case 'microbe' :
                        parsedProductType = ['MICROBE_DETECTION', 'MICROBE_DETECTION'];
                        break;
                }

                if (this.checkProperty(response, parsedProductType)) {
                    this._globalSearchStatus$.next(GlobalSearchStatusEnum.doneWithResults);
                } else {
                    this._globalSearchStatus$.next(GlobalSearchStatusEnum.doneWithWrongApp);
                }
            } else {
                this._globalSearchStatus$.next(GlobalSearchStatusEnum.doneWithResults);
            }
        }
    }

    checkProperty(response: TaqmanGlobalSearchResponseInterface, parsedProductTypes: string[]): boolean {
        let checked = false;
        if (response.productTypeHits && parsedProductTypes) {
            for (const value of parsedProductTypes) {
                if (Object.prototype.hasOwnProperty.call(response.productTypeHits, value) && (response.productTypeHits[value] > 0)) {
                    checked = true;
                    break;
                }
            }
        }
        return checked;
    }

    doSearch(): void {
        this.doSearchProducts();
        this.doSearchProductCounts();
    }

    setFilterPillState(filterIndex?: number, state?: boolean) {
        const totalFilters = this.getLocalFilters('filterState')?.length;

        if (totalFilters) {
            const currentState = Array(totalFilters).fill(false);
            currentState[filterIndex] = state;

            this.setLocalFilters({ overlayVisible: currentState });
        }
    }

    setLocalFilters(nextState: ITempFilters) {
        this._tempFilters$.next({ ...this._tempFilters$.getValue(), ...nextState });
    }

    getLocalFilters(key: string) {
        const selectedValue = this._tempFilters$.getValue()[key];
        return this._shallowCopy(selectedValue);
    }

    parseSuggestedKeywords(suggestedKeywords: string) {
        let parsed: string[] = [];
        if (suggestedKeywords && suggestedKeywords.length > 0) {
            parsed = suggestedKeywords.split(',');
        }
        return parsed;
    }

    handleLoadMore(): void {

        this.searchStatus$.next(SearchStatusEnum.loadingMore);

        const searchParams: SearchParamsInterface = this.searchParams$.getValue();
        const offset = this.searchResults$.getValue()?.products[searchParams.productSubtype]?.length?.toString() || '25';
        const taqmanSearchParams: TaqmanSearchRequestInterface = {
            keyword: searchParams.keyword,
            offset: offset,
            productType: searchParams.productType,
            productSubtype: searchParams.productSubtype,
            sortBy: searchParams.sortBy,
            filters: searchParams.filters,
            sortOrder: searchParams.sortOrder,
            filterState: searchParams.filterState
        }

        this.searchService.doSearch(taqmanSearchParams, searchParams.filterState).subscribe((response) => {

            const products = [...this.searchResults$.getValue().products[searchParams.productSubtype], ...response.products[searchParams.productSubtype]];

            this.searchResults$.next({
                ...this.searchResults$.getValue(),
                products: {
                    [searchParams.productSubtype]: products
                }
            });

            this.changeSearchParams({ offset: offset });

            this.searchStatus$.next(SearchStatusEnum.doneWithResults);

        });
    }

    handleFilterSearch(typeAheadKeyword: string, filterToSearch: string, filterIndex: number): void {
        const searchParams: SearchParamsInterface = this.searchParams$.getValue();
        const filters = this.searchResults$.getValue().filters;
        const filter = filters[filterIndex];
        const filterSearchParams: FilterSearchRequestInterface = {
            searchKeyword: searchParams.keyword,
            filterToSearch: filterToSearch,
            typeAheadKeyword: typeAheadKeyword.toUpperCase(),
            productType: searchParams.productType.toUpperCase(),
            productSubtype: searchParams.productSubtype.toUpperCase(),
        }

        this.apiService.filterSearch(filterSearchParams).subscribe(
            {
                next: (response) => {
                    this.processFilterSearch(response, filter, typeAheadKeyword, filters, filterIndex);
                },
                error: (err) => {
                    console.log('Error', err);
                    this.processFilterSearch(err, filter, typeAheadKeyword, filters, filterIndex);
                }
            }
        );
    }

    processFilterSearch(response: any, filter: any, typeAheadKeyword: string, filters: any, filterIndex: number) {
        const navigators = this.hasError(response) ? this.getNavigators() : response;
        const updated = {
            ...filter,
            navigators: navigators
        };
        const newFilters = [...filters.slice(0, filterIndex), updated, ...filters.slice(filterIndex + 1)];
        const geneFilterKeywords = Array(newFilters.length).fill('');
        geneFilterKeywords.fill(typeAheadKeyword, filterIndex, filterIndex + 1);
        const overLay = this.getLocalFilters('overlayVisible');
        overLay[filterIndex] = true;

        this.searchResults$.next({ ...this.searchResults$.getValue(), filters: newFilters });
        this.setLocalFilters({ geneFilterKeyword: geneFilterKeywords, overlayVisible: overLay });
        this.setFilterState(newFilters);
    }

    getNavigators() {
        const currentFilters = this.getLocalFilters('filters');
        let navigators = this._shallowCopy(this.initialGeneFilters['navigators']);

        // Only trigger if genes filter gets searched
        // The value that is filtered from the gene filter has to be added in the current filters
        for (let i = 0; i < currentFilters.length; i++) {
            if (currentFilters[i]['code'] === 'PrimaryGeneSymbolCaps') {
                navigators.unshift(currentFilters[i]['serverNavigator']);
                navigators = navigators.filter((value, index, self) => index === self.findIndex((t) => t.name === value.name));
                navigators.pop();
            }
        }

        return navigators;
    }

    hasError(response) {
        return Object.prototype.hasOwnProperty.call(response, 'details');
    }

    updateResultCount(params: ResultCountInterface) {
        if ((params.productType.toLowerCase() === 'ge' && params.productSubType.toLowerCase() === 'ge') ||
            (params.productType.toLowerCase() === 'snp' && params.productSubType.toLowerCase() === 'snp') ||
            (params.productType.toLowerCase() === 'cnv' && params.productSubType.toLowerCase() === 'cnv') ||
            (params.productType.toLowerCase() === 'microrna' && params.productSubType.toLowerCase() === 'microrna_primary') ||
            (params.productType.toLowerCase() === 'microrna' && params.productSubType.toLowerCase() === 'microrna_classic') ||
            (params.productType.toLowerCase() === 'microrna' && params.productSubType.toLowerCase() === 'microrna_advanced')) {
            this.setSearchResultCounts({ assayCount: params.count });
        } else {
            this.setSearchResultCounts({ arrayCount: params.count });
        }
    }

    setSearchResultCounts(nextState: SearchCountsInterface) {
        this._searchResultCountsState$.next({ ...this._searchResultCountsState$.getValue(), ...nextState });
    }

    setSearchParams(searchParams: Partial<SearchParamsInterface>): void {
        this.searchParams$.next({ ...initialSearchParams, ...searchParams });
    }

    resetSearch(): void {
        this.resetFilters();
        this.resetSearchResults();
        this.unselectAllProducts();
    }

    resetProductCountSearch(searchCountsParams: TaqmanProductCountRequestInterface): void {
        this.searchParams$.next({ ...initialProductCountParams, ...searchCountsParams });
        this._globalSearchResults$.next(null);
    }

    resetSearchResults(): void {
        this.searchResults$.next(null);
    }

    resetGlobalSearchResults(): void {
        this._globalSearchResults$.next(null);
        this._globalSearchStatus$.next(GlobalSearchStatusEnum.initial);
    }

    resetFilters(): void {
        this.setLocalFilters({ filters: [], filterState: [], selectedOptions: [] })
    }

    getKeyword(): string {
        return this.searchParams$.getValue().keyword;
    }

    setKeyword(newKeyword: string): void {
        this.searchParams$.next({ keyword: newKeyword });
    }

    selectAllProducts(): void {
        const productSubType = this.searchResults$.getValue().productSubType;
        const products = this.searchResults$.value.products[productSubType] ?? [];
        this.selectedResults$.next({ products: [...products] });
        this.allSelectedResults$.next(true);
    }

    unselectAllProducts(): void {
        this.selectedResults$.next({ selectedProducts: [], products: [] });
        this.allSelectedResults$.next(false);
    }

    addSelectedProduct(product: TaqmanProductInterface): void {
        const products = this.selectedResults$.value?.products || [];
        products.push(product);

        this.selectedResults$.next({ ...this.selectedResults$.getValue(), products: products });
        this.allSelectedResults$.next(false);
    }

    removeSelectedProduct(product: TaqmanProductInterface): void {
        const products = this.selectedResults$.value?.products || [];
        const index = products.findIndex((item) => item.productID == product.productID);
        if (index > -1) {
            products.splice(index, 1);
            this.selectedResults$.next({ ...this.selectedResults$.getValue(), products: products });
        }
        this.allSelectedResults$.next(false);
    }

    getSelectedProductsCount(): number {
        const products = this.selectedResults$.value?.products || [];
        return Object.keys(products).length;
    }

    getProductsCount(): number {
        const productTypeEnum = this.searchResults$.getValue().productTypeEnum;
        const products = this.searchResults$.value?.products[productTypeEnum] || [];
        return Object.keys(products).length;
    }

    getSelectedProductIds(): string[] {
        const products = this.selectedResults$.value?.products || [];
        return products.map(product => product.productID);
    }

    getSelectedAssayIds(): string[] {
        const products = this.selectedResults$.value?.products || [];
        return products.map(product => product.assayID);
    }

    getSelectedAssayIdsWithCatalogNumbers(): AssayCatalog[] {
        const products = this.selectedResults$.value?.products || [];
        return products.map(product => {
            return {
                'assayId': this.getProductType() === 'cnv' || this.getProductType() === 'ge' || this.getProductType() === 'microrna' ? product.productID : product.assayID,
                'catalogNumber': product.selectedCatalogNumber ? product.selectedCatalogNumber : product.defaultCatalogNum
            }
        });

    }

    areAllResultsSelected(): boolean {
        return this.allSelectedResults$.getValue();
    }

    shouldDisplaySubtractIcon(): boolean {
        const selectedProductsCount = this.getSelectedProductsCount();
        return selectedProductsCount > 0 && this.getProductsCount() > selectedProductsCount;
    }

    shouldDisplayWarning(): boolean {

        if (this.limitWarning == this.getSelectedProductsCount()) {
            this.warningShown = true;
            return true;
        }

        return false;
    }

    shouldDisplayFirstWarning(): boolean {
        if (this.firstShown === true) {
            return false;
        }

        if (this.getSelectedProductsCount() === 1) {
            this.firstShown = true;
            return true;
        }

        return false;
    }

    getDyeOptions(dyeSizes: any): Dropdown[] {
        if (dyeSizes) {
            const dyes = Object.keys(dyeSizes);
            return dyes.map((dye) => makeDropdownOption(dye, dye, dye));
        }

        return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getDyeOptionsGEAssays(dyeSizes: any): Dropdown[] {
        const dropdownOptions: Dropdown[] = [];
        const dyes = Object.keys(dyeSizes);

        for (const dye of dyes) {
            if (Object.prototype.hasOwnProperty.call(dyeSizes, dye)) {
                const sizes = dyeSizes[dye];
                if (sizes.length > 0) {
                    // multiplexEligible is used to determine if the dye is enabled for GE assays
                    const availability = sizes[0].multiplexEligible === 'Y' ? 'multiplexEligible' : '';
                    const option = makeDropdownOption(dye, dye, availability);
                    dropdownOptions.push(option);
                }
            }
        }
        return dropdownOptions;
    }

    getSizeOptions(dyeSizes: any, dye: string, _availability: string): Dropdown[] {
        if (dyeSizes && dye) {
            const sizes = dyeSizes[dye];
            return sizes.map((size) => makeDropdownOption(size.catalogNumber, size.size, size.availability));
        }

        return null;
    }

    getSizeOptionsSNP(dyeSizes: any, _availability: string): Dropdown[] {
        if (dyeSizes) {
            return dyeSizes.filter(sizeItem => sizeItem.displayOnSearch).map((size) => makeDropdownOption(size.catalogNumber, size.size, size.availability));
        }

        return null;
    }

    makeSizeOptions(dyeSizes: any, _availability: string): Dropdown[] {
        if (dyeSizes) {
            return dyeSizes.map((size) => makeDropdownOption(size.catalogNumber, size.size, size.availability));
        }

        return null;
    }

    _manipulateSelectedFilters(): Map<string, string> {
        const mappedFilters = new Map();
        const filters = this.getFilters();
        filters.forEach((filter: SelectItem) => {
            if (filter['app'] && filter['code']) {
                mappedFilters.set(filter['code'], `${mappedFilters.get(filter['code'])}${filter.value['name']},`);
            }
        })
        return mappedFilters;
    }

    _getFilteredString(newFilters: Map<string, string>): string {
        let strFilters = '';

        newFilters.forEach((value, key) => {
            value = value.substring(0, value.length - 1);
            value = value.replace('undefined', '');

            strFilters += `${key}:${value}~`;
        })

        return strFilters;
    }

    getFilters(): SelectItem[] {
        return this._tempFilters$.value.filters;
    }

    setModalFilters(nextState: any[], totalCount: number) {
        this._modalFilters$.next({
            ...this._modalFilters$.getValue(),
            filters: nextState,
            totalResultsCount: totalCount
        });
    }

    getProductType() {
        return this.searchParams$.getValue().productType;
    }

    selectCatalogNumber(product: GEProductInterface | GXProductInterface | SNPProductInterface, selectedCatalogNumber: string): void {
        let products;
        products = this.searchResults$.value?.products[product.productType == 'GT' ? ProductTypeEnum.snp : ProductTypeEnum.ge] || [];
        const index = products.findIndex((item) => item.productID == product.productID);
        if (index > -1) {
            products[index].selectedCatalogNumber = selectedCatalogNumber;
            const searchResults = this.searchResults$.getValue();
            products = this.searchResults$.value?.products[product.productType == 'GT' ? ProductTypeEnum.snp : ProductTypeEnum.ge] || [];
            searchResults.products[product.productType == 'GT' ? ProductTypeEnum.snp : ProductTypeEnum.ge] = [...products];
            this.searchResults$.next({ ...searchResults });
        }

        const selectedProducts = this.selectedResults$.value?.products || [];
        const selectedIndex = selectedProducts.findIndex((item) => item.productID == product.productID);
        if (selectedIndex > -1) {
            selectedProducts[index].selectedCatalogNumber = selectedCatalogNumber;
            this.selectedResults$.next({ ...this.selectedResults$.getValue(), products: selectedProducts });
        }
    }

    selectSize(product: GEProductInterface | SNPProductInterface, selectedSize: string): void {
        const products = this.searchResults$.value?.products[ProductTypeEnum.ge] || [];
        const index = products.findIndex((item) => item.productID == product.productID);
        if (index > -1) {
            products[index].selectedSize = selectedSize;
            const searchResults = this.searchResults$.getValue();
            searchResults.products[ProductTypeEnum.ge] = [...products];
            this.searchResults$.next({ ...searchResults });
        }
    }

    updateQuantity(product: GXProductInterface | MicrornaArrayProductInterface, qty: number, type: ProductTypeEnum | ProductSubtypeEnum): void {
        const products: GXProductInterface[] | MicrornaArrayProductInterface[] = this.searchResults$.value?.products[type] || [];
        const index = products.findIndex((item) => item.productID == product.productID);
        if (index > -1) {
            products[index].quantity = qty;
            const searchResults = this.searchResults$.getValue();
            searchResults.products[type] = [...products];
            this.searchResults$.next({ ...searchResults });
        }

        const selectedProducts = this.selectedResults$.value?.products || [];
        const selectedIndex = selectedProducts.findIndex((item) => item.productID == product.productID);
        if (selectedIndex > -1) {
            selectedProducts[index].quantity = qty;
            this.selectedResults$.next({ ...this.selectedResults$.getValue(), products: selectedProducts });
        }
    }

    _shallowCopy(objOrArray: any) {
        return JSON.parse(JSON.stringify(objOrArray));
    }

    notifyChanges(): void {
        this.searchResults$.next(this.searchResults$.getValue());
    }

    getLoadedProductsCount(): number {
        return this.searchResults$.getValue()?.products?.[this.searchParams$.getValue()?.productSubtype]?.length || 0;
    }

}
